import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import ClientFormDialog from '../components/common/ClientFormDialog';
import { AddTile, EmptyState, InitialsAvatar } from '../components/common/ui';
import ClientService from '../services/ClientService';
import { useToast } from '../contexts/ToastContext';
import { usePageRefresh } from '../components/layout/pullToRefresh';

const PAGE_SIZE = 24;

function Line({ icon, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, color: '#64748B' }}>
            <Box sx={{ display: 'flex', color: '#94A3B8', flexShrink: 0 }}>{icon}</Box>
            <Typography noWrap component="div" sx={{ fontSize: 12.5, minWidth: 0 }}>{children}</Typography>
        </Box>
    );
}

function ClientCard({ client, onOpen }) {
    const { t } = useTranslation();
    const place = [client.address, client.city].filter(Boolean).join(', ');
    return (
        <Paper elevation={0} onClick={onOpen} sx={{
            border: '1px solid #E2E8F0', borderRadius: '14px', p: 2.25, cursor: 'pointer', minHeight: 150,
            display: 'flex', flexDirection: 'column', gap: 1.5, transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: '#CBD5E1', boxShadow: '0 8px 20px -14px rgba(30,58,82,0.45)' },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <InitialsAvatar name={client.name} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 700, color: '#0F172A' }}>{client.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {client.type === 'COMPANY' && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 11, fontWeight: 700, color: '#427898', flexShrink: 0 }}>
                                <ApartmentOutlinedIcon sx={{ fontSize: 13 }} />{t('clients.type.COMPANY')}
                            </Box>
                        )}
                        {client.idNumber && <Typography noWrap sx={{ fontSize: 11.5, color: '#94A3B8' }}>{client.idNumber}</Typography>}
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                {client.phone && (
                    <Line icon={<PhoneOutlinedIcon sx={{ fontSize: 15 }} />}>
                        {/* Tap-to-call without opening the client. */}
                        <Box component="a" href={`tel:${client.phone}`} onClick={(e) => e.stopPropagation()}
                             sx={{ color: '#427898', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{client.phone}</Box>
                    </Line>
                )}
                {client.email && <Line icon={<MailOutlineIcon sx={{ fontSize: 15 }} />}>{client.email}</Line>}
                {place && <Line icon={<PlaceOutlinedIcon sx={{ fontSize: 15 }} />}>{place}</Line>}
            </Box>
            <Box sx={{ mt: 'auto', pt: 1.25, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <WorkOutlineIcon sx={{ fontSize: 15, color: client.jobCount ? '#427898' : '#CBD5E1' }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: client.jobCount ? '#334155' : '#CBD5E1' }}>
                    {t('clients.jobsTitle', { count: client.jobCount })}
                </Typography>
            </Box>
        </Paper>
    );
}

export default function ClientsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [searchText, setSearchText] = useState('');
    const [q, setQ] = useState('');
    const [data, setData] = useState({ items: [], total: 0, page: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const h = setTimeout(() => setQ(searchText), 300);
        return () => clearTimeout(h);
    }, [searchText]);

    // First page on every new search; "Load more" appends the next one.
    const load = useCallback(() => {
        setLoading(true);
        return ClientService.list({ q: q || undefined, page: 0, size: PAGE_SIZE })
            .then(d => setData({ items: d.items, total: d.total, page: 0 }))
            .catch(() => showToast(t('common.loadError'), 'error'))
            .finally(() => setLoading(false));
    }, [q, showToast, t]);
    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const loadMore = () => {
        setLoadingMore(true);
        ClientService.list({ q: q || undefined, page: data.page + 1, size: PAGE_SIZE })
            .then(d => setData(prev => ({ items: [...prev.items, ...d.items], total: d.total, page: prev.page + 1 })))
            .catch(() => showToast(t('common.loadError'), 'error'))
            .finally(() => setLoadingMore(false));
    };

    return (
        <Layout>
            <PageHeader title={t('clients.title')} count={data.total || null}
                        action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{t('clients.new')}</Button>} />

            <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '14px', p: 1.5, mb: 2.5 }}>
                <TextField size="small" fullWidth placeholder={t('clients.searchPlaceholder')} value={searchText} onChange={(e) => setSearchText(e.target.value)}
                           slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment> } }} />
            </Box>

            {loading && !data.items.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : !data.items.length ? (
                q ? <EmptyState icon={<SearchIcon />} title={t('clients.emptyFiltered')} />
                  : <EmptyState icon={<PeopleAltOutlinedIcon />} title={t('clients.empty')} text={t('clients.emptyHint')}
                                action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{t('clients.new')}</Button>} />
            ) : (
                <>
                    <Box sx={{
                        display: 'grid', gap: 2, opacity: loading ? 0.6 : 1,
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
                    }}>
                        {/* Add-new tile (Plankton's customers page). */}
                        {!q && <AddTile label={t('clients.new')} icon={<AddIcon fontSize="small" />} onClick={() => setCreateOpen(true)} />}
                        {data.items.map(c => <ClientCard key={c.id} client={c} onOpen={() => navigate(`/clients/${c.id}`)} />)}
                    </Box>
                    {data.items.length < data.total && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}
                                    startIcon={loadingMore ? <CircularProgress size={16} /> : null}>
                                {t('clients.loadMore', { count: data.total - data.items.length })}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            <ClientFormDialog open={createOpen} onClose={() => setCreateOpen(false)}
                              onSaved={(c) => { setCreateOpen(false); navigate(`/clients/${c.id}`); }} />
        </Layout>
    );
}
